/**
 * CampaignImageInput — the campaign form's Image field as one composite
 * control (epic 095, D8 — the operator's binding UX spec): a drag-and-drop
 * zone with the URL input directly beneath it, no tabs, both paths always
 * visible, and a single preview.
 *
 * The form's model keeps ONE value — the `bannerImageUrl` string; this
 * component is controlled on it exactly like the plain Input it replaced.
 * A chosen File exists only here, transiently (the zone's name chip), and
 * collapses into the minted URL:
 *
 * - Upload fires on file select, not inside Save (README D6): the choice
 *   clears the URL (mutual exclusion) and POSTs immediately. Success writes
 *   the returned URL into the model and keeps the original filename for the
 *   preview caption; failure renders the server/interrupted message inline
 *   and never writes the model — whatever the value held before the failure
 *   stays (never silently cleared).
 * - Mutual exclusion both ways (D8): a file choice clears the URL; typing a
 *   non-empty URL resets the file chip and marks any in-flight request stale
 *   — its late response is ignored when it lands (same for Remove and for
 *   clearing the chip, the r1 L5 stale guard).
 * - The preview shows only when the model value is non-empty: thumbnail +
 *   caption (the uploaded filename when known, else the URL) + Remove, which
 *   clears the value and the upload state.
 * - `onUploadingChange` is the parent's Save gate signal (D6: Save is blocked
 *   while an upload is in flight). It also fires false when the component
 *   unmounts mid-upload, so a style switch or navigation cannot leave the
 *   form's Save permanently gated on an orphaned request.
 *
 * `FileDropZone` is used as-is (D8 — its visible native input is its
 * keyboard/accessibility contract, QA #43); it has no `disabled` prop, so
 * `disabled` here gates the URL input and Remove only.
 */

import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { FileDropZone } from './file-drop-zone';
import { Button } from './ui/button';
import { Input } from './ui/input';

interface UploadState {
  status: 'idle' | 'uploading' | 'error';
  filename?: string;
  message?: string;
}

export interface CampaignImageInputProps {
  /** Forwarded to the drop zone's native input (pairs with its visible label). */
  id: string;
  /** The model value — the single bannerImageUrl string ('' = no image). */
  value: string;
  /** The only model write: the typed URL, the minted upload URL, or ''. */
  onChange: (url: string) => void;
  /** Gates the URL input and Remove (the zone itself stays active — D8). */
  disabled?: boolean;
  /** Save-gate signal: true while an upload is in flight (README D6). */
  onUploadingChange?: (uploading: boolean) => void;
}

export function CampaignImageInput({
  id,
  value,
  onChange,
  disabled = false,
  onUploadingChange,
}: CampaignImageInputProps) {
  // The chosen File lives only here — transient, never in the form's model
  // (D1/D6: a File collapses into the minted URL). It feeds the zone's chip.
  const [file, setFile] = useState<File | null>(null);
  const [upload, setUpload] = useState<UploadState>({ status: 'idle' });
  // True when the current model value was minted by a finished upload — the
  // zone's clear affordance clears the value only for its own upload's URL.
  const [valueFromUpload, setValueFromUpload] = useState(false);

  // Stale-response guard: only the request whose id is still in
  // `activeRequestRef` may write the model or the upload state. Typing a URL,
  // Remove, clearing the chip and unmount all invalidate the in-flight request.
  const activeRequestRef = useRef(0);
  const nextRequestIdRef = useRef(0);
  // Mirrors the last value fired through onUploadingChange, so transitions are
  // edge-triggered (one true, one false per upload) even across re-renders.
  const uploadingRef = useRef(false);
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;

  const setUploadingActive = (active: boolean) => {
    if (uploadingRef.current === active) return;
    uploadingRef.current = active;
    onUploadingChangeRef.current?.(active);
  };

  // An unmount (style switched away, navigation) releases the parent's Save
  // gate and marks the in-flight request stale — its continuation must not
  // write the model after this component is gone.
  useEffect(() => {
    return () => {
      activeRequestRef.current = 0;
      if (uploadingRef.current) {
        uploadingRef.current = false;
        onUploadingChangeRef.current?.(false);
      }
    };
  }, []);

  const handleUrlChange = (text: string) => {
    onChange(text);
    setValueFromUpload(false);
    // Mutual exclusion (D8): typing a URL resets the file and the upload
    // state — the in-flight request is marked stale and ignored on landing.
    if (text.trim() !== '') {
      setFile(null);
      setUpload({ status: 'idle' });
      activeRequestRef.current = 0;
      setUploadingActive(false);
    }
  };

  const startUpload = (chosen: File) => {
    const requestId = ++nextRequestIdRef.current;
    activeRequestRef.current = requestId;
    setFile(chosen);
    setUpload({ status: 'uploading', filename: chosen.name });
    setUploadingActive(true);
    // Mutual exclusion (D8): a file choice clears the URL — one model value,
    // and the preview must not show the old image while it is being replaced.
    onChange('');
    setValueFromUpload(false);

    void api
      .uploadCampaignImage(chosen)
      .then((uploaded) => {
        if (activeRequestRef.current !== requestId) return; // stale
        setUploadingActive(false);
        setUpload({ status: 'idle', filename: uploaded.originalFilename });
        setValueFromUpload(true);
        onChange(uploaded.url);
      })
      .catch((err: unknown) => {
        if (activeRequestRef.current !== requestId) return; // stale
        setUploadingActive(false);
        // Failure never writes the model (D6): whatever the value held before
        // the attempt stays; only the error becomes readable inline.
        setUpload({
          status: 'error',
          filename: chosen.name,
          message:
            err instanceof ApiError && err.message
              ? err.message
              : 'The image upload failed. Try again.',
        });
      });
  };

  const handleFileChange = (chosen: File | null) => {
    if (chosen) {
      startUpload(chosen);
      return;
    }
    // The zone's clear affordance. Only an upload that finished and produced
    // the current value ties the chip to the model — clearing it clears the
    // value. An in-flight choice is abandoned (stale-guarded); an error keeps
    // both its message and the model value.
    setFile(null);
    if (upload.status === 'uploading') {
      activeRequestRef.current = 0;
      setUpload({ status: 'idle' });
      setUploadingActive(false);
    } else if (upload.status === 'idle' && valueFromUpload) {
      activeRequestRef.current = 0;
      setUpload({ status: 'idle' });
      setValueFromUpload(false);
      onChange('');
    }
  };

  const handleRemove = () => {
    onChange('');
    setFile(null);
    setUpload({ status: 'idle' });
    setValueFromUpload(false);
    activeRequestRef.current = 0;
    setUploadingActive(false);
  };

  return (
    <div className="space-y-2">
      <FileDropZone
        id={id}
        label="Image file"
        accept="image/*"
        dropzoneText="Drag an image here or click to browse"
        value={file}
        onChange={handleFileChange}
      />
      <div className="space-y-1.5">
        <label htmlFor={`${id}-url`} className="text-xs font-semibold text-ink">
          or paste an image URL
        </label>
        <Input
          id={`${id}-url`}
          type="url"
          placeholder="https://…"
          value={value}
          onChange={(e) => handleUrlChange(e.target.value)}
          disabled={disabled}
        />
        {upload.status === 'uploading' && upload.filename && (
          <p className="text-xs text-muted" role="status">
            Uploading {upload.filename}…
          </p>
        )}
        {upload.status === 'error' && upload.message && (
          <p className="text-sm text-destructive" role="alert">
            {upload.message}
          </p>
        )}
      </div>
      {value !== '' && (
        <div className="rounded-lg bg-surface p-3 hairline">
          <img
            src={value}
            alt="Image preview"
            className="mx-auto max-h-48 w-auto max-w-full object-contain"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <span
              className="min-w-0 truncate text-xs text-muted"
              title={valueFromUpload && upload.filename ? upload.filename : value}
            >
              {valueFromUpload && upload.filename ? upload.filename : value}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemove}
              disabled={disabled}
            >
              Remove
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
